import React, { useEffect } from "react";
import { Boxes as BoxesIcon, Plane, Scale, Ship, Weight } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import CargoLinesEditor, { computeCargoTotals } from "./CargoLinesEditor";
import { FieldLabel, RadioCard, SectionLabel } from "./FormPrimitives";

export const ShipmentModeCard = ({ form, setField }) => {
  const isAir = form.shipment_mode === "AIR";
  const isSea = form.shipment_mode === "SEA";

  // Auto-sync totals from cargo lines whenever they change.
  useEffect(() => {
    if (!form.cargo_lines || form.cargo_lines.length === 0) return;
    const t = computeCargoTotals(form.cargo_lines, isAir);
    // Only overwrite if the derived value differs (avoid infinite loops via reference).
    if (Number(form.total_weight || 0) !== t.total_weight) setField("total_weight", String(t.total_weight));
    if (Number(form.total_cbm || 0) !== t.total_cbm) setField("total_cbm", String(t.total_cbm));
    if (isAir && Number(form.total_chargeable_weight || 0) !== t.total_chargeable_weight) {
      setField("total_chargeable_weight", String(t.total_chargeable_weight));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.cargo_lines, isAir]);

  return (
    <Card className="border-zinc-200 shadow-sm">
      <div className="p-6 md:p-8">
        <SectionLabel hint="Step 2 / 5">Shipment Mode</SectionLabel>
        <div className="grid grid-cols-2 gap-3 mb-5">
          <RadioCard testid="radio-mode-air" checked={isAir} onClick={() => setField("shipment_mode", "AIR")} icon={Plane} label="Air" sublabel="DA" />
          <RadioCard testid="radio-mode-sea" checked={isSea} onClick={() => setField("shipment_mode", "SEA")} icon={Ship} label="Sea" sublabel="DS" />
        </div>

        {(isAir || isSea) && (
          <>
            <CargoLinesEditor
              value={form.cargo_lines || []}
              onChange={(next) => setField("cargo_lines", next)}
              isAir={isAir}
            />

            <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <FieldLabel htmlFor="total_weight" icon={Weight}>Total Weight (kg)</FieldLabel>
                <Input
                  id="total_weight"
                  data-testid="input-total-weight"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={form.total_weight || ""}
                  onChange={(e) => setField("total_weight", e.target.value)}
                  className="h-11 font-mono"
                />
              </div>
              {isSea && (
                <div>
                  <FieldLabel htmlFor="total_cbm" icon={BoxesIcon}>Total CBM</FieldLabel>
                  <Input
                    id="total_cbm"
                    data-testid="input-total-cbm"
                    type="number"
                    step="0.001"
                    min="0"
                    placeholder="0.000"
                    value={form.total_cbm || ""}
                    onChange={(e) => setField("total_cbm", e.target.value)}
                    className="h-11 font-mono"
                  />
                </div>
              )}
              {isAir && (
                <div className="md:col-span-2">
                  <FieldLabel htmlFor="total_chargeable_weight" icon={Scale}>Total Chargeable Weight (kg)</FieldLabel>
                  <Input
                    id="total_chargeable_weight"
                    data-testid="input-chargeable-weight"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={form.total_chargeable_weight || ""}
                    onChange={(e) => setField("total_chargeable_weight", e.target.value)}
                    className="h-11 font-mono"
                  />
                </div>
              )}
            </div>
            <p className="text-[10px] text-zinc-500 mt-1.5 font-mono">
              Totals auto-fill from cargo lines. Empty cargo list ⇒ enter totals manually. You can override at any time.
            </p>
          </>
        )}
      </div>
    </Card>
  );
};
